import { Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Suspense, useEffect, useState } from "react";
import { useAppAuth } from "@/context/AppAuthContext";
import { LogOut, Smartphone } from "lucide-react";
import AppBottomNav from "@/components/app/AppBottomNav";
import PWAInstallBanner from "@/components/app/PWAInstallBanner";
import AppLock from "@/pages/app/AppLock";
import { isMobileDevice } from "@/lib/deviceId";
import { sincronizarTodas } from "@/lib/offlineQueue";

// Guarda o app no celular para abrir sem internet (arquivos do app + página inicial)
function prepararOffline() {
  import("@/pages/app/AppBaterPonto").catch(() => {});
  setTimeout(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const urls = performance.getEntriesByType("resource").map((e) => e.name)
        .filter((u) => u.startsWith(window.location.origin) && /\/static\/|\.(png|svg|jpeg|json)$/.test(u));
      reg.active?.postMessage({ type: "CACHE_URLS", urls, shell: "/app" });
    } catch (_) { /* sem service worker */ }
  }, 4000);
}

export default function AppLayout() {
  const { session, loading, pedirSaida, locked, unlock } = useAppAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [bypassDesktop, setBypassDesktop] = useState(false);

  // Garante que o tema claro (de versão anterior) é removido no boot do app.
  useEffect(() => {
    try { document.documentElement.classList.remove("dp-app-light"); } catch (_) {}
  }, []);

  // Service worker (lazy, só uma vez por boot do app)
  useEffect(() => {
    if ("serviceWorker" in navigator && location.pathname.startsWith("/app")) {
      navigator.serviceWorker.register("/app-sw.js", { scope: "/app" }).catch(() => {});
    }
  }, [location.pathname]);

  useEffect(() => {
    if ("serviceWorker" in navigator) prepararOffline();
  }, []);

  // Envio automático das batidas guardadas no celular (ao abrir, ao voltar a internet e a cada 20s)
  useEffect(() => {
    if (!session) return undefined;
    const sync = () => { sincronizarTodas(session?.app_user?.id).catch(() => {}); };
    sync();
    const t = setInterval(sync, 20000);
    const onVis = () => { if (document.visibilityState === "visible") sync(); };
    window.addEventListener("online", sync);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); window.removeEventListener("online", sync); document.removeEventListener("visibilitychange", onVis); };
  }, [session]);

  // 🚫 BLOQUEIO: App do funcionário SÓ no celular.
  if (!bypassDesktop && !isMobileDevice()) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-5">
          <div className="h-20 w-20 mx-auto rounded-2xl bg-emerald-600/20 border border-emerald-500/40 flex items-center justify-center">
            <Smartphone size={40} className="text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold">App exclusivo para celular</h1>
          <p className="text-sm text-slate-400 leading-relaxed">
            Por segurança e conformidade com a <b>Portaria 671/2021 MTE</b>, o DigiPonto só permite
            bater ponto diretamente do celular do funcionário (com GPS obrigatório e vinculação
            exclusiva ao próprio aparelho).
          </p>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-left text-xs text-slate-400 space-y-2">
            <div className="flex items-start gap-2">
              <span className="text-emerald-400">1.</span>
              <span>No celular, abra o navegador e acesse <b className="text-emerald-300">digiponto.net/app</b></span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-emerald-400">2.</span>
              <span>Toque em "Adicionar à tela inicial" para instalar o app</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-emerald-400">3.</span>
              <span>Permita acesso ao GPS quando solicitado</span>
            </div>
          </div>
          <button
            onClick={() => setBypassDesktop(true)}
            data-testid="btn-desktop-bypass"
            className="text-[11px] text-slate-500 hover:text-slate-400 underline">
            Continuar mesmo assim (apenas para testes do admin)
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">
        Carregando…
      </div>
    );
  }

  if (!session && location.pathname !== "/app/login") {
    return <Navigate to="/app/login" replace />;
  }
  if (session && location.pathname === "/app/login") {
    return <Navigate to="/app" replace />;
  }
  if (session && locked) {
    return <AppLock onUnlock={unlock} />;
  }

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden bg-slate-950 text-slate-100">
      {session && (
        <header className="shrink-0 z-20 bg-slate-900/80 backdrop-blur border-b border-slate-800">
          <div className="max-w-md mx-auto px-4 h-16 flex items-center justify-between gap-2">
            <button
              data-testid="app-header-home"
              onClick={() => navigate("/app")}
              className="flex items-center gap-3 text-left min-w-0 flex-1"
            >
              {/* Logo horizontal INTEIRA com cantos arredondados */}
              <img
                src="/digiponto-logo-horizontal.jpeg"
                alt="DigiPonto"
                className="h-10 w-auto max-w-[140px] object-contain rounded-md bg-white px-1.5 py-1 shrink-0"
                data-testid="app-header-logo"
              />
              <div className="min-w-0">
                <div className="text-[10px] text-slate-400 leading-none truncate">
                  {session.empresa?.razao_social || session.empresa?.nome_fantasia}
                </div>
                <div className="text-xs font-bold leading-tight truncate">
                  {session.funcionario?.nome?.split(" ").slice(0, 2).join(" ")}
                </div>
              </div>
            </button>
            <button
              data-testid="app-header-logout"
              onClick={() => { pedirSaida(); }}
              className="h-9 w-9 grid place-items-center rounded-md bg-slate-800 hover:bg-slate-700 shrink-0"
              title="Sair"
            >
              <LogOut size={14} />
            </button>
          </div>
        </header>
      )}
      {/* Só o conteúdo rola; cabeçalho e menu inferior ficam sempre na tela */}
      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="app-main-scroll">
        <div className="max-w-md mx-auto px-4 py-4 pb-6">
          <Suspense fallback={<div className="py-10 text-center text-slate-500 text-sm">Carregando…</div>}>
            <Outlet />
          </Suspense>
        </div>
      </main>
      {session && <AppBottomNav />}
      {session && <PWAInstallBanner />}
    </div>
  );
}
